import { BalanceAnalysisInterpretation, IBalanceAnalysisResult } from '../models/financing-request-balance.model';
import { BalancePeriodUtils } from './balance-period.utils';
import { Constants } from './constants';

/** Estados del job que todavía no son terminales (si backend responde 200 en lugar de 204). */
const NON_TERMINAL_STATUSES = ['PENDING', 'PROCESSING', 'IN_PROGRESS', 'QUEUED', 'RUNNING'];

/**
 * Traduce la respuesta de `/core/portal/jobs/{jobId}/status` del análisis de balance a una
 * decisión para el bloque (función pura, sin estado, para poder testearla aislada).
 */
export class BalanceAnalysisInterpreter {

  /** Para `JobPollingService.pollJob({ isPending })`. */
  static isPending(body: IBalanceAnalysisResult | null): boolean {
    const status = BalanceAnalysisInterpreter.unwrap(body)?.status?.toUpperCase();
    return !!status && NON_TERMINAL_STATUSES.includes(status);
  }

  /**
   * La falla viene como wrapper `{ jobId, status, errorMessage, result }` y el éxito plano.
   * Si en algún momento el éxito viene anidado en `result`, se desenvuelve.
   */
  static unwrap(body: IBalanceAnalysisResult | null | undefined): IBalanceAnalysisResult | null {
    if (!body) return null;
    if (body.result && typeof body.result === 'object') {
      return { ...body.result, status: body.result.status ?? body.status, jobId: body.jobId };
    }
    return body;
  }

  /**
   * Normaliza el dictamen: mayúsculas, sin acentos y con '_' en lugar de espacios.
   * 'REQUIERE REVISIÓN MANUAL' → 'REQUIERE_REVISION_MANUAL'.
   */
  static normalizeDictamen(dictamen: string | null | undefined): string | null {
    if (!dictamen) return null;
    return dictamen
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .trim().toUpperCase().replace(/\s+/g, '_');
  }

  /** Período de cierre: extractedData.period → balanceData.fechaCierre → fechaCierre (formato anterior). */
  static extractPeriod(result: IBalanceAnalysisResult | null): string | null {
    if (!result) return null;
    return BalancePeriodUtils.normalize(result.extractedData?.period ?? null)
      ?? BalancePeriodUtils.normalize(result.extractedData?.balanceData?.fechaCierre ?? null)
      ?? BalancePeriodUtils.normalize(result.fechaCierre ?? result.periodoCierre ?? null);
  }

  static interpret(
    body: IBalanceAnalysisResult | null | undefined,
    minConfidence: number = Constants.BALANCE_MIN_CONFIDENCE
  ): BalanceAnalysisInterpretation {
    const result = BalanceAnalysisInterpreter.unwrap(body);
    const status = result?.status?.toUpperCase();
    const confidence = typeof result?.confidence === 'number' ? result.confidence : null;
    const documentUrl = result?.documentUrl ?? null;
    const justification = result?.justificacion?.trim() || null;
    const period = BalanceAnalysisInterpreter.extractPeriod(result);
    const base = { period, confidence, documentUrl, justification };

    if (!result || status === 'FAILED') {
      return { kind: 'FAILED', period: null, confidence: null, documentUrl: null, justification: null };
    }

    // Contrato actual: el dictamen del backend manda (ya contempla confianza, empresa y vigencia).
    switch (BalanceAnalysisInterpreter.normalizeDictamen(result.dictamen)) {
      case 'APROBADO':
        return period ? { ...base, kind: 'DETECTED' } : { ...base, kind: 'NO_PERIOD' };
      case 'RECHAZADO':
        return { ...base, kind: 'REJECTED' };
      case 'REQUIERE_REVISION_MANUAL':
        return { ...base, kind: 'MANUAL_REVIEW' };
    }

    // Sin dictamen (formato anterior): se decide por flags, período y confianza.
    const decision = result.aiDecision?.status?.toUpperCase();
    if (result.esBalance === false || decision === 'REJECTED') {
      return { ...base, kind: 'REJECTED', justification: justification ?? result.aiDecision?.reason ?? null };
    }

    if (!period) {
      return { ...base, kind: 'NO_PERIOD' };
    }

    // Sin confianza informada se asume suficiente (el backend ya dio un período).
    if (confidence !== null && confidence < minConfidence) {
      return { ...base, kind: 'LOW_CONFIDENCE' };
    }

    return { ...base, kind: 'DETECTED' };
  }
}
